import { useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;

export const useVTTSocket = ({ sessionId, playerName, onTokenMoved, onHpUpdated, onTokenAdded, onTokenRemoved, onFogChanged, onSpellsChanged, onAssetsChanged, onPingReceived, onShapeDrawn, onDiceRolled, onHandoutShared, onPlayerJoined, onPlayerLeft }) => {
  const socketRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;

    const socket = io(SOCKET_URL, {
      path: "/api/socket.io",
      withCredentials: true,
      transports: ["polling", "websocket"],
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("join_session", { session_id: sessionId, player_name: playerName || "DM" });
    });

    socket.on("token_moved", (data) => onTokenMoved?.(data));
    socket.on("hp_updated", (data) => onHpUpdated?.(data));
    socket.on("token_added", (data) => onTokenAdded?.(data));
    socket.on("token_removed", (data) => onTokenRemoved?.(data));
    socket.on("fog_changed", (data) => onFogChanged?.(data));
    socket.on("spells_changed", (data) => onSpellsChanged?.(data));
    socket.on("assets_changed", (data) => onAssetsChanged?.(data));
    socket.on("ping_received", (data) => onPingReceived?.(data));
    socket.on("shape_drawn", (data) => onShapeDrawn?.(data));
    socket.on("dice_rolled", (data) => onDiceRolled?.(data));
    socket.on("handout_shared", (data) => onHandoutShared?.(data));
    socket.on("player_joined", (data) => onPlayerJoined?.(data));
    socket.on("player_left", (data) => onPlayerLeft?.(data));

    return () => {
      socket.emit("leave_session", { session_id: sessionId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [sessionId]);

  const emitMoveToken = useCallback((tokenId, x, y) => {
    socketRef.current?.emit("move_token", { session_id: sessionId, token_id: tokenId, x, y });
  }, [sessionId]);

  const emitUpdateHp = useCallback((tokenId, hp) => {
    socketRef.current?.emit("update_hp", { session_id: sessionId, token_id: tokenId, hp });
  }, [sessionId]);

  const emitAddToken = useCallback((token) => {
    socketRef.current?.emit("add_token_sync", { session_id: sessionId, token });
  }, [sessionId]);

  const emitRemoveToken = useCallback((tokenId) => {
    socketRef.current?.emit("remove_token_sync", { session_id: sessionId, token_id: tokenId });
  }, [sessionId]);

  const emitFogUpdate = useCallback((fogAreas) => {
    socketRef.current?.emit("fog_update", { session_id: sessionId, fog_areas: fogAreas });
  }, [sessionId]);

  const emitSpellsUpdate = useCallback((spells) => {
    socketRef.current?.emit("spells_update", { session_id: sessionId, spells });
  }, [sessionId]);

  const emitAssetsUpdate = useCallback((assets) => {
    socketRef.current?.emit("assets_update", { session_id: sessionId, assets });
  }, [sessionId]);

  const emitPing = useCallback((x, y) => {
    socketRef.current?.emit("ping_map", { session_id: sessionId, x, y, player_name: playerName });
  }, [sessionId, playerName]);

  const emitMeasure = useCallback((shapeData) => {
    socketRef.current?.emit("measure_shape", { session_id: sessionId, ...shapeData });
  }, [sessionId]);

  const emitDiceRoll = useCallback((rollData) => {
    socketRef.current?.emit("dice_rolled", { session_id: sessionId, ...rollData });
  }, [sessionId]);

  const emitShareHandout = useCallback((handoutData) => {
    socketRef.current?.emit("share_handout", { session_id: sessionId, ...handoutData });
  }, [sessionId]);

  return {
    emitMoveToken,
    emitUpdateHp,
    emitAddToken,
    emitRemoveToken,
    emitFogUpdate,
    emitSpellsUpdate,
    emitAssetsUpdate,
    emitPing,
    emitMeasure,
    emitDiceRoll,
    emitShareHandout,
    isConnected: !!socketRef.current?.connected,
  };
};
